import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import Link from 'redux-first-router-link';
import Price from './Price';
import cartAnalytics from '../Selection/cartAnalytics';
import Button from '../Elements/Button/Button';
import { addEvent, removeEvent, addClass, removeClass } from '../../utils/dom';
import { prefix, translate } from '../../utils/locale';
import { deepClone } from '../../utils/object';
import { applyVAT, formatPrice } from '../../utils/string';
import { addToBag } from '../../actions/checkout';
import s from './Purchase.scss';

class Purchase extends Component {
	constructor(props) {
		super(props);

		this.state = {
			formError: false,
			formErrorHover: false,
			itemBeingAdded: false,
			itemAdded: false,
			itemAddedError: false,
			quantity: 1,
		};

		this.formElement = React.createRef();
	}

	componentDidMount() {
		addEvent(document.querySelector('body'), 'click.clearFormError', (e) => {
			if (this.formElement.current && !this.formElement.current.contains(e.target) && this.state.formError) {
				e.stopPropagation();
				e.preventDefault();
				this.setState({
					formError: false,
				});
			}
		});

		addEvent(window, 'keydown.clearFormError', (e) => {
			if (e.key === 'Escape') {
				this.setState({
					formError: false,
				});
			}
		});
	}

	componentWillUnmount() {
		removeEvent(document.querySelector('body'), 'click.clearFormError');
		removeEvent(window, 'keydown.clearFormError');
	}

	addToBagHandler = (e) => {
		e.preventDefault();
		const { setup, cache, currentPath, productPageSelection } = this.props;
		const { quantity } = this.state;
		const currentCategory = currentPath.slice(0, currentPath.lastIndexOf('/'));

		// All sizes must be added for purchase to be successful.
		if (productPageSelection.product) {
			const cachedProduct = cache[Object.keys(productPageSelection.product)[0]];
			this.setState({ itemBeingAdded: true });

			const lineItems = [];
			const cachedItems = [];
			let customAttributes = {};

			if (cachedProduct.tags && cachedProduct.tags.indexOf('glides') !== -1) {
				if (!customAttributes.customAttributes) customAttributes = { customAttributes: [] };
				customAttributes.customAttributes = [
					...customAttributes.customAttributes,
					{
						key: `${cachedProduct.name} Connected To`,
						value: `${productPageSelection.message}`,
					},
				];
			}

			Object.keys(productPageSelection).forEach((type) => {
				Object.keys(productPageSelection[type]).forEach((cacheId) => {
					if (type === 'message') return;

					// Get selected product's SKU for connecting line item to product
					if (type !== 'product') {
						customAttributes = {
							customAttributes: [
								{
									key: `${cache[cacheId].name} for`,
									value: `${cachedProduct.name}`,
								},
							],
						};
					}

					lineItems.push({
						variantId: productPageSelection[type][cacheId],
						quantity,
						...customAttributes,
					});

					cachedItems.push(deepClone(cache[cacheId]));
				});
			});

			this.props.addToBag(lineItems, cachedItems, currentCategory).then((response) => {
				this.setState({
					itemBeingAdded: false,
					itemAdded: true,
				});

				cartAnalytics(lineItems, cachedItems, setup, currentCategory, `${parseInt(response?.data?.totalPriceV2?.amount || 0, 10)}`, false, quantity);

				window.setTimeout(() => {
					addClass(document.body, 'u_addedToBag');

					window.setTimeout(() => {
						removeClass(document.body, 'u_addedToBag');
					}, 4000);

					window.setTimeout(() => {
						this.setState({ itemAdded: false });
					}, 2000);
				}, 500);
				return true;
			}).catch((err) => {
				this.setState({ itemAddedError: true });

				console.log('ERROR: add to bag failed ', err);
				Sentry.captureMessage('ADD TO BAG ERROR: failed to add');

				window.setTimeout(() => {
					this.setState({ itemAddedError: false });
				}, 2000);
			});

			return;
		}

		this.setState({
			formError: true,
		});
	}

	addToBagMouseEnterHandler = () => {
		const { productPageSelection } = this.props;

		if (!productPageSelection.product) {
			this.setState({
				formErrorHover: true,
			});
		}
	}

	addToBagMouseLeaveHandler = () => {
		this.setState({
			formErrorHover: false,
		});
	}

	adjustQuantityHandler = (e) => {
		const value = parseInt(e.currentTarget.value, 10);
		this.setState((prevState) => {
			let quantity = prevState.quantity + value;
			if (quantity < 1) quantity = 1;

			return {
				quantity,
			};
		});
	}

	render() {
		const { setup, soldOut, cache, productPageSelection, removeSelectedItemHandler, variant } = this.props;
		const { formError, formErrorHover, itemBeingAdded, itemAdded, itemAddedError, quantity } = this.state;

		const selectedProductCacheId = Object.keys(productPageSelection.product)[0];
		const selectedProduct = productPageSelection.product ? cache[Object.keys(productPageSelection.product)[0]] : {};
		const selectedSize = selectedProduct.sizes.filter((size) => productPageSelection.product[selectedProductCacheId] === size.id)[0];

		const totalPrice = Object.keys(productPageSelection).reduce((acc, type) => {
			if (type === 'message') return acc;
			if (type === 'product') return acc + parseFloat(applyVAT(setup, selectedSize.price, false, 5));
			return acc + Object.keys(productPageSelection[type]).reduce((acc2, cacheId) => (
				acc2 + parseFloat(applyVAT(setup, cache[cacheId].price, false, 5))
			), 0);
		}, 0) * quantity;

		let unavailableInCurrentMarket = false;
		if (selectedSize?.availability === '["US"]' && setup.country === 'US') unavailableInCurrentMarket = true;
		if (selectedSize?.availability === '["EU"]' && setup.country !== 'US') unavailableInCurrentMarket = true;

		let buttonText = translate(setup, 'itemAdd');
		if ((formError || formErrorHover) && !productPageSelection.product) buttonText = translate(setup, 'chooseSize');
		if (soldOut) buttonText = translate(setup, 'soldOut');
		if (unavailableInCurrentMarket) buttonText = translate(setup, 'soldOut');
		if (itemAdded) buttonText = translate(setup, 'itemAdded');
		if (itemAddedError) buttonText = translate(setup, 'itemAddedError');
		if (itemBeingAdded) buttonText = translate(setup, 'itemBeingAdded');
		if (setup.unableToSendToCountry) buttonText = translate(setup, 'itemAddUnavailable');
		let leadTime = variant.metafields.leadTime || selectedProduct.leadTime;
		let market = setup?.market;
		if (typeof market === 'number') market = String(typeof setup.market);
		if (!Array.isArray(market)) market = [market];
		if (market.indexOf('65580597415') !== -1 && (variant.metafields.leadTimeEu || selectedProduct.leadTimeEu)) leadTime = variant.metafields.leadTimeEu || selectedProduct.leadTimeEu;

		return (
			<form className={s.purchase} onSubmit={this.addToBagHandler} ref={this.formElement}>
				<div className={[s.purchase_submit, (!(formError || formErrorHover) ? s.purchase_submit__default : null), (soldOut ? s.purchase_submit__soldOut : null)].join(' ')}>
					<div className={s.purchase_submit_quantity}>
						<button className={s.purchase_submit_quantity_adjust} type="button" aria-label="Decrease quantity" value="-1" onClick={this.adjustQuantityHandler} disabled={quantity <= 1} />
						<span className={s.purchase_submit_quantity_total}>{ quantity }</span>
						<button className={[s.purchase_submit_quantity_adjust, s.purchase_submit_quantity_adjust__increase].join(' ')} type="button" aria-label="Increase quantity" value="1" onClick={this.adjustQuantityHandler} />
					</div>
					<div className={s.purchase_submit_selectionItems}>
						{
							productPageSelection.product ? (
								<div className={s.purchase_submit_selectionItem}>
									<span className={s.purchase_submit_selectionItem_text}>{ `${quantity}x ${selectedProduct.name}${selectedProduct.variant ? ` — ${selectedProduct.variant}` : ''}` }</span>
									<div className={s.purchase_submit_selectionItem_price}>
										<Price price={selectedSize.price} setup={setup} />
									</div>
								</div>
							) : (
								<div className={s.purchase_submit_selectionItem}>
									<span className={s.purchase_submit_selectionItem_text}>{ translate(setup, 'noProductSelected') }</span>
								</div>
							)
						}
						{
							productPageSelection.seatPad && Object.keys(productPageSelection.seatPad).map((cacheId) => {
								const selectedSeatPad = cache[cacheId];

								return (
									<a className={[s.purchase_submit_selectionItem, s.purchase_submit_selectionItem__removable].join(' ')} href={`#remove-seatPad-${cacheId}`} onClick={removeSelectedItemHandler}>
										<span className={s.purchase_submit_selectionItem_text}>{ `${quantity}x ${selectedSeatPad.name}${selectedSeatPad.variant ? ` — ${selectedSeatPad.variant}` : ''}` }</span>
										<div className={s.purchase_submit_selectionItem_price}>
											<Price price={selectedSeatPad.price} setup={setup} />
										</div>
									</a>
								);
							})
						}
						{
							productPageSelection.glides && Object.keys(productPageSelection.glides).map((cacheId) => {
								const selectedGlides = cache[cacheId];

								return (
									<a className={[s.purchase_submit_selectionItem, s.purchase_submit_selectionItem__removable].join(' ')} href={`#remove-glides-${cacheId}`} onClick={removeSelectedItemHandler} title={`${quantity}x ${selectedGlides.variant ? `${selectedGlides.variant} — ` : ''}${selectedGlides.name}`}>
										<span className={s.purchase_submit_selectionItem_text}>{ `${quantity}x ${selectedGlides.variant ? `${selectedGlides.variant} — ` : ''}${selectedGlides.name}` }</span>
										<div className={s.purchase_submit_selectionItem_price}>
											<Price price={selectedGlides.price} setup={setup} />
										</div>
									</a>
								);
							})
						}
					</div>
					<Button variantClasses={['button__purchase', (itemBeingAdded ? 'button__loading' : null)]} disabled={setup.unableToSendToCountry || unavailableInCurrentMarket} mouseEnterEventHandler={this.addToBagMouseEnterHandler} mouseLeaveEventHandler={this.addToBagMouseLeaveHandler}>
						<span>{ buttonText }</span>
					</Button>
					{ setup.unableToSendToCountry && <div className={s.purchase_submit_summary}>{translate(setup, 'purchaseUnavailable')}</div> }
					{ !setup.unableToSendToCountry && !unavailableInCurrentMarket && productPageSelection.product && <div className={s.purchase_submit_summary}>{`${translate(setup, 'total')}: ${formatPrice(setup, totalPrice)} — ${translate(setup, 'leadTime')}: ${leadTime}`}</div> }
					<Link className={s.purchase_submit_trade} to={prefix(setup, translate(setup, 'checkoutTradeLink'))}>
						<span>{ translate(setup, 'checkoutTradeBadge') }</span>
						<span>{ translate(setup, 'productTradeMessage') }</span>
					</Link>
				</div>
			</form>
		);
	}
}

const mapState = (state, props) => ({
	...props,
});

export default hot(module)(connect(mapState, { addToBag })(Purchase));
